import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useResponsive } from "../../customHooks/useResponsive";

import { ScrollToTopButton } from "../../styledComponents/common";

const ScrollToTop = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const debounceRef = useRef(null);
  const location = useLocation();
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  // Define pages where scroll to top should be active
  const scrollEnabledPages = ["/timesheet/daily"];

  const isScrollEnabled = scrollEnabledPages.some(page =>
    location.pathname.includes(page),
  );

  const checkScrollVisibility = useCallback(() => {
    if (!isScrollEnabled) {
      setShowScrollButton(false);
      return;
    }

    let scrollContainer = window;
    let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let windowHeight = window.innerHeight;
    let documentHeight = document.documentElement.scrollHeight;

    // On mobile/tablet, use the mobile scroll container
    if (isMobileOrTablet) {
      const mobileScrollContent = document.querySelector(
        "[data-scroll-container]"
      );
      if (mobileScrollContent) {
        scrollContainer = mobileScrollContent;
        scrollTop = mobileScrollContent.scrollTop;
        windowHeight = mobileScrollContent.clientHeight;
        documentHeight = mobileScrollContent.scrollHeight;
      }
    }

    // Calculate how close to bottom (within 50px tolerance)
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    const isNearBottom = distanceFromBottom <= 50;

    // Show button when user is at or very close to the bottom of the page
    // and there's substantial content (page is scrollable)
    const shouldShow = isNearBottom && documentHeight > windowHeight + 200;

    setShowScrollButton(shouldShow);
  }, [isScrollEnabled, isMobileOrTablet]);

  useEffect(() => {
    if (showScrollButton) {
      setShouldRender(true);
      setIsAnimatingIn(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      setIsAnimatingIn(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [showScrollButton]);

  useEffect(() => {
    if (!isScrollEnabled) {
      setShowScrollButton(false);
      return;
    }

    // Initial check
    const timer = setTimeout(() => {
      checkScrollVisibility();
    }, 100);

    let scrollContainer = window;

    // On mobile/tablet, use the mobile scroll container
    if (isMobileOrTablet) {
      const mobileScrollContent = document.querySelector(
        "[data-scroll-container]"
      );
      if (mobileScrollContent) {
        scrollContainer = mobileScrollContent;
      }
    }

    const handleScroll = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        checkScrollVisibility();
      }, 150);
    };

    const handleResize = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        checkScrollVisibility();
      }, 200);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      clearTimeout(timer);
      scrollContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isScrollEnabled, checkScrollVisibility, isMobileOrTablet]);

  // Reset state when location changes
  useEffect(() => {
    setShowScrollButton(false);
    setIsAnimatingIn(false);
    setShouldRender(false);
  }, [location.pathname]);

  const scrollToTop = () => {
    if (isMobileOrTablet) {
      const mobileScrollContent = document.querySelector(
        "[data-scroll-container]"
      );
      if (mobileScrollContent) {
        mobileScrollContent.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!shouldRender && !isAnimatingIn) return null;

  return (
    <ScrollToTopButton $isVisible={isAnimatingIn} onClick={scrollToTop}>
      <span>Scroll to top</span>
    </ScrollToTopButton>
  );
};

export default ScrollToTop;
